import { useUnitStore } from "@/lib/unit";
import { applyCaseToProcess, defaultCaseStage } from "@/ui/case-to-process";
import { ChecklistPicker } from "@/ui/checklists";
import { useDraft } from "@/lib/draft";
import { useFields } from "@/lib/fields";
import { ExportButtons, fetchAllPages } from "@/ui/export";
import { usePageSize } from "@/lib/pagesize";
import { staleRange, useRememberedSearch } from "@/lib/prefs";
import { FilterNotice } from "@/ui/filter-notice";
import { useEffect, useRef, useState } from "react";
import { confirmar, toast } from "@/ui/feedback";
import { LoadStrip } from "@/ui/load-strip";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { Link, navigate, useSearch, useTitle } from "@/lib/router";
import { addDaysIso, dateOnly, fmtDate, fmtDateTime, isOverdue, NATURE_LABEL, STATE_LABEL, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import {
  AreaSelect, Badge, Card, DataTable, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Pager, ReasonDialog, SearchBox, Tabs, TypeSelect, UserSelect, useAct, type EntityRef, CopyButton, FatalDate, relatedFatalTitle, type RelatedFatal,
} from "@/ui/kit";
import { DocumentsPanel } from "./shared";
import { ActivityMails } from "./MailPages";
import { MenuBoard, ModeSwitch, useBoardPref } from "@/ui/board";
import { DeadlineCalcModal, useActivityDateFill, type CalcApply } from "@/ui/deadline-calc";
import { FlowStepCard, OutcomePicker, flowOutcomeBlocked, useFlowStep } from "@/ui/bpm";
import { NewProcessModal } from "./ProcessPages";

export interface ActivityRow {
  relatedFatal?: RelatedFatal | null;
  firstStartedAt?: string | null; finishedAt?: string | null; cancelledAt?: string | null; cancelledBy?: string | null; cancelReason?: string | null;
  completedBy?: string | null; completedByOther?: boolean;
  checklist?: Array<{ t: string; d: boolean; by?: string; at?: string }> | null; pointsAwarded?: string | number | null; pointsUserId?: string | null; pointsLate?: boolean | null;
  id: string; number: number; title: string; description: string | null; state: string; typeId: string | null;
  parentId: string | null; personId: string | null; caseId: string | null; processId: string | null;
  responsibleUserId: string | null; reviewerUserId: string | null;
  internalDueAt: string | null; plannedAt: string | null; fatalDueAt: string | null;
  priority: string; risk: string; version: number; overdue?: boolean; fatalOverdue?: boolean;
  completedAt: string | null; createdAt: string;
}

type View = "minhas" | "fila" | "atrasadas" | "revisao" | "todas" | "concluidas";

const PRIORITY_TONE: Record<string, "bad" | "warn" | "muted"> = { alta: "bad", media: "warn", baixa: "muted" };

/** Situação da atividade. "A fazer" que já foi iniciada alguma vez aparece como "Pausada". */
export function stateBadge(state: string, firstStartedAt?: string | null): JSX.Element {
  if (state === "atribuida" && firstStartedAt) return <span title={`Iniciada em ${fmtDateTime(firstStartedAt)} e pausada`}><Badge tone="warn">⏸ Pausada</Badge></span>;
  if (state === "cancelada") return <span title="Encerrada sem ser feita"><Badge tone="muted">✕ Cancelada</Badge></span>;
  const tone = state === "concluida" ? "ok" : state === "em_revisao" || state === "em_andamento" ? "info" : state === "aguardando_atribuicao" ? "warn" : state === "aguardando_dependencia" ? "muted" : "neutral";
  return <Badge tone={tone}>{state === "concluida" ? "✓ " : state === "em_andamento" ? "▶ " : ""}{STATE_LABEL[state] ?? state}</Badge>;
}

/** Lista reutilizável (páginas de processo, caso, pessoa). */
export function ActivityList({ filter, view = "todas", includeClosed, empty }: {
  filter: Record<string, string | undefined>; view?: View; includeClosed?: boolean; empty?: string;
}): JSX.Element {
  const { userName } = useLookups();
  const { data, error } = useQuery({
    queryKey: ["atividades", view, filter, includeClosed],
    queryFn: () => api.get<{ items: ActivityRow[]; total: number }>(`/atividades${qs({ view, ...filter, includeClosed, take: 100 })}`),
  });
  return (
    <>
      <ErrorBox error={error} />
      <DataTable
        rows={data?.items}
        rowKey={(a) => a.id}
        onRowClick={(a) => navigate(`/atividades/${a.id}`)}
        rowClass={(a) => (a.fatalOverdue || a.overdue ? "row-bad" : a.state === "concluida" || a.state === "cancelada" ? "row-muted" : a.state === "aguardando_dependencia" ? "row-waiting" : "")}
        empty={empty ?? "Nenhuma atividade."}
        columns={[
          { key: "n", label: "#", width: 56, render: (a) => a.number },
          { key: "t", label: "Atividade", render: (a) => a.title },
          { key: "r", label: "Responsável", render: (a) => userName(a.responsibleUserId) },
          { key: "i", label: "Interno", width: 96, render: (a) => <span className={a.overdue ? "text-bad" : ""}>{fmtDate(a.internalDueAt)}</span> },
          { key: "f", label: "Fatal", width: 96, sort: (a) => a.fatalDueAt ?? a.relatedFatal?.date ?? null, render: (a) => <FatalDate own={a.fatalDueAt} related={a.relatedFatal} overdue={a.fatalOverdue} open={!["concluida", "cancelada"].includes(a.state)} /> },
          { key: "s", label: "Situação", width: 150, render: (a) => <>{stateBadge(a.state, a.firstStartedAt)}{a.completedByOther && <div className="small muted">por {userName(a.completedBy).split(" ")[0]}</div>}</> },
        ]}
      />
    </>
  );
}

/** "12/10 · por Fulano (responsável: Beltrano)" quando quem concluiu não era o responsável. */
export function CompletedBy({ a }: { a: ActivityRow }): JSX.Element {
  const { userName } = useLookups();
  if (a.state === "cancelada") return (
    <span className="small" title={a.cancelReason ?? undefined}>
      <Badge tone="muted">✕ Cancelada</Badge> {a.cancelledAt ? fmtDate(a.cancelledAt) : ""}{a.cancelledBy && <span className="muted"> · {userName(a.cancelledBy).split(" ")[0]}</span>}
      {a.cancelReason && <div className="muted small" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 240 }}>{a.cancelReason}</div>}
    </span>
  );
  return (
    <span className="small">
      {a.completedAt ? fmtDate(a.completedAt) : "—"}
      {a.completedByOther && a.completedBy && <span className="tag tag-amber" title={`Responsável: ${userName(a.responsibleUserId)}`} style={{ marginLeft: 6 }}>por {userName(a.completedBy).split(" ")[0]}</span>}
    </span>
  );
}

export function ActivitiesPage(): JSX.Element {
  useTitle("Atividades");
  const [search, set] = useSearch();
  const view = (search.get("visao") as View) || "minhas";
  const q = search.get("q") ?? "";
  const skip = Number(search.get("skip") ?? 0);
  const responsible = search.get("resp") ?? "";
  // Concluídas: todo o histórico por padrão (período é opcional).
  const from = search.get("de") ?? "";
  const to = search.get("ate") ?? "";
  const fim = search.get("fim") ?? "";
  const preset = !from && !to ? "tudo" : to === todayIso() && from === addDaysIso(todayIso(), -30) ? "30" : to === todayIso() && from === addDaysIso(todayIso(), -90) ? "90" : to === todayIso() && from === addDaysIso(todayIso(), -365) ? "365" : "outro";
  // Filtros extras e ordenação (ficam na URL: dá para salvar o link)
  const f = {
    typeId: search.get("tipo") ?? "", priority: search.get("prio") ?? "", areaId: search.get("area") ?? "",
    fatal: search.get("fatal") ?? "", dateField: search.get("data") ?? "", sort: search.get("ordem") ?? "", dir: search.get("dir") ?? "",
  };
  const extraCount = [f.typeId, f.priority, f.areaId, f.fatal, view !== "concluidas" && (from || to) ? "p" : "", view !== "minhas" && view !== "concluidas" && view !== "todas" ? responsible : ""].filter(Boolean).length;
  const [showFilters, setShowFilters] = useState(extraCount > 0);
  const periodOn = !!from || !!to || !!f.dateField;

  const { userName, typeOf, can, areaName } = useLookups();
  // Filtros lembrados por pessoa (a visão/aba não: abre sempre onde a pessoa clicar).
  const remembered = useRememberedSearch("atividades", ["resp", "tipo", "prio", "area", "fatal", "data", "de", "ate", "fim", "ordem", "dir"], { dateKeys: ["de", "ate"] });
  const [creating, setCreating] = useState(search.get("nova") === "1");
  const board = useBoardPref("atividades");

  const pageSize = usePageSize();
  // A unidade do menu lateral vale nas visões do escritório (Todas e Fila).
  const storeUnit = useUnitStore((x) => x.unitId);
  const unitId = (view === "todas" || view === "fila") && storeUnit ? storeUnit : undefined;
  const { unitName } = useLookups();
  const params = {
    view, q, responsibleUserId: responsible || undefined, unitId, fim: view === "concluidas" && fim ? fim : undefined,
    from: periodOn ? from || undefined : undefined, to: periodOn ? to || undefined : undefined,
    typeId: f.typeId || undefined, priority: f.priority || undefined, areaId: f.areaId || undefined, fatal: f.fatal || undefined,
    dateField: f.dateField || undefined, sort: f.sort || undefined, dir: f.dir || undefined,
  };  const { data, error } = useQuery({
    enabled: board.mode === "lista",
    queryKey: ["atividades", "page", skip, pageSize, params],
    queryFn: () => api.get<{ items: ActivityRow[]; total: number; take: number; skip: number }>(
      `/atividades${qs({ ...params, skip, take: pageSize })}`),
  });

  // Aviso de filtros: nas visões de pendências, o que ficou fora do filtro (e o que está vencido/fatal hoje).
  const filterKeys = remembered.active.filter((k) => k !== "ordem" && k !== "dir");
  const riskOn = view !== "concluidas" && view !== "revisao" && filterKeys.length > 0;
  const base = { view, unitId };
  const { data: rAll } = useQuery({ enabled: riskOn, queryKey: ["atividades", "risco", base], queryFn: () => api.get<{ total: number }>(`/atividades${qs({ ...base, take: 1 })}`) });
  const { data: rUrg } = useQuery({ enabled: riskOn, queryKey: ["atividades", "risco-urg", base], queryFn: () => api.get<{ total: number }>(`/atividades${qs({ ...base, overdue: true, take: 1 })}`) });
  const { data: rUrgIn } = useQuery({ enabled: riskOn, queryKey: ["atividades", "risco-urg-in", params], queryFn: () => api.get<{ total: number }>(`/atividades${qs({ ...params, overdue: true, take: 1 })}`) });
  const ALABEL: Record<string, (v: string) => [string, string?]> = {
    resp: (v) => ["Responsável", userName(v)], tipo: (v) => ["Tipo", typeOf(v)?.name ?? "tipo"], prio: (v) => ["Prioridade", v], area: (v) => ["Área", areaName(v)],
    fatal: (v) => ["Prazo fatal", v === "1" ? "com" : "sem"], data: (v) => ["Data usada", ({ qualquer: "qualquer", interno: "prazo interno", fatal: "prazo fatal", planejada: "planejada", criacao: "criação" } as Record<string, string>)[v] ?? v],
    de: (v) => ["De", fmtDate(v)], ate: (v) => ["Até", fmtDate(v)], fim: (v) => ["Encerramento", v === "feitas" ? "só concluídas" : "só canceladas"],
    ordem: (v) => ["Ordenação", v], dir: (v) => ["Ordem", v === "asc" ? "crescente" : "decrescente"],
  };
  const noticeItems = remembered.active.map((k) => { const [label, value] = ALABEL[k]!(remembered.values[k]!); return { label, value }; });
  const notice = (
    <FilterNotice items={noticeItems} restored={remembered.restored} onClear={() => { remembered.clear(); set({ skip: null }); }}
      stale={view !== "concluidas" && (from || to) ? staleRange(from || null, to || null) : null}
      risk={riskOn && rAll && data ? { hidden: Math.max(0, rAll.total - data.total), urgent: rUrg && rUrgIn ? Math.max(0, rUrg.total - rUrgIn.total) : 0,
        noun: ["atividade em aberto", "atividades em aberto"], urgentNoun: ["atividade vencida ou com prazo fatal hoje", "atividades vencidas ou com prazo fatal hoje"] } : null} />
  );

  return (
    <div>
      <PageHeader title="Atividades" actions={<button className="btn primary" onClick={() => setCreating(true)}>Nova atividade</button>} />
      <Tabs<View>
        value={view}
        onChange={(v) => set({ visao: v, skip: null })}
        tabs={[
          { key: "minhas", label: "Minhas" },
          { key: "atrasadas", label: "Atrasadas" },
          { key: "revisao", label: "Para revisar" },
          { key: "fila", label: "Fila sem responsável", hidden: !can("atividade.atribuir") },
          { key: "todas", label: "Todas" },
          { key: "concluidas", label: "Concluídas" },
        ]}
      />
      <div className="toolbar">
        <SearchBox value={q} onChange={(v) => set({ q: v, skip: null })} placeholder="Buscar pelo título…" />
        <button className={`btn ${showFilters ? "primary" : ""}`} onClick={() => setShowFilters(!showFilters)}>Filtros{extraCount ? ` (${extraCount})` : ""}</button>
        <label className="row nowrap small">Ordenar
          <select className="input" value={f.sort} onChange={(e) => set({ ordem: e.target.value || null, dir: null, skip: null })}>
            <option value="">Padrão ({view === "concluidas" ? "mais recentes" : "prazo fatal"})</option>
            {view === "concluidas" && <option value="encerramento">Data de encerramento</option>}
            <option value="fatal">Prazo fatal</option><option value="interno">Prazo interno</option><option value="planejada">Data planejada</option>
            <option value="prioridade">Prioridade</option><option value="criacao">Criação</option><option value="numero">Número</option>
            <option value="titulo">Título (A–Z)</option><option value="situacao">Situação</option>
          </select>
          {f.sort && <button className="btn sm" title="Inverter a ordem" onClick={() => set({ dir: (f.dir || (f.sort === "criacao" || f.sort === "numero" ? "desc" : "asc")) === "asc" ? "desc" : "asc", skip: null })}>
            {(f.dir || (f.sort === "criacao" || f.sort === "numero" ? "desc" : "asc")) === "asc" ? "↑" : "↓"}</button>}
        </label>
        {view === "concluidas" && <>
          <select className="input" style={{ width: "auto" }} value={fim} onChange={(e) => set({ fim: e.target.value || null, skip: null })} title="Canceladas contam como encerradas, mas não feitas">
            <option value="">Concluídas e canceladas</option><option value="feitas">Só concluídas (feitas)</option><option value="canceladas">Só canceladas (não feitas)</option>
          </select>
          <select className="input" style={{ width: "auto" }} value={preset} onChange={(e) => {
            const v = e.target.value;
            if (v === "tudo") set({ de: null, ate: null, skip: null });
            else if (v === "outro") setShowFilters(true);
            else set({ de: addDaysIso(todayIso(), -Number(v)), ate: todayIso(), skip: null });
          }}>
            <option value="tudo">Todo o histórico</option><option value="30">Últimos 30 dias</option><option value="90">Últimos 90 dias</option><option value="365">Últimos 12 meses</option>
            <option value="outro">{preset === "outro" ? `${from ? fmtDate(from) : "…"} a ${to ? fmtDate(to) : "…"}` : "Escolher período…"}</option>
          </select>
        </>}
        {(view === "todas" || view === "concluidas") && <div style={{ width: 220 }}><UserSelect value={responsible} onChange={(v) => set({ resp: v, skip: null })} emptyLabel="Todos os responsáveis" /></div>}
        {unitId && <span className="tag tag-blue" title="Unidade escolhida no menu lateral">Unidade: {unitName(unitId)} <button className="link-btn small" onClick={() => useUnitStore.getState().set("")}>✕</button></span>}
        <span className="spacer" />
        <ExportButtons title="Atividades" load={async () => {
          const all = await fetchAllPages<ActivityRow>((sk, tk) => `/atividades${qs({ ...params, skip: sk, take: tk })}`);
          return { columns: ["Nº", "Atividade", "Tipo", "Responsável", "Prazo interno", "Prazo fatal", "Situação", "Prioridade", "Concluída em"],
            rows: all.map((a) => [a.number, a.title, typeOf(a.typeId)?.name ?? "", userName(a.responsibleUserId), fmtDate(a.internalDueAt), a.fatalDueAt ? fmtDate(a.fatalDueAt) : a.relatedFatal ? `${fmtDate(a.relatedFatal.date)} (ligado)` : "", STATE_LABEL[a.state] ?? a.state, a.priority, a.completedAt ? fmtDate(a.completedAt) : ""]) };
        }} />
        <ModeSwitch mode={board.mode} onChange={board.setMode} />
      </div>
      {notice}
      {showFilters && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="grid cols-4" style={{ gap: 10 }}>
            <Field label="Tipo"><TypeSelect value={f.typeId || null} onChange={(v) => set({ tipo: v, skip: null })} /></Field>
            <Field label="Prioridade">
              <select className="input" value={f.priority} onChange={(e) => set({ prio: e.target.value || null, skip: null })}>
                <option value="">Todas</option><option value="urgente">Urgente</option><option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
              </select>
            </Field>
            <Field label="Área"><AreaSelect value={f.areaId || null} onChange={(v) => set({ area: v, skip: null })} allowEmpty /></Field>
            <Field label="Prazo fatal">
              <select className="input" value={f.fatal} onChange={(e) => set({ fatal: e.target.value || null, skip: null })}>
                <option value="">Com e sem</option><option value="1">Só com prazo fatal</option><option value="0">Sem prazo fatal</option>
              </select>
            </Field>
            {view !== "minhas" && view !== "todas" && view !== "concluidas" && (
              <Field label="Responsável"><UserSelect value={responsible} onChange={(v) => set({ resp: v, skip: null })} emptyLabel="Todos" /></Field>
            )}
            <Field label="Período — data">
              <select className="input" value={f.dateField} onChange={(e) => set({ data: e.target.value || null, skip: null })}>
                <option value="">{view === "concluidas" ? "Encerramento (conclusão/cancelamento)" : "Qualquer data (interno, fatal, planejada)"}</option>
                <option value="interno">Prazo interno</option><option value="fatal">Prazo fatal</option><option value="planejada">Data planejada</option><option value="criacao">Criação</option>
              </select>
            </Field>
            <Field label="De"><input type="date" className="input" value={from} onChange={(e) => set({ de: e.target.value || null, skip: null })} /></Field>
            <Field label="Até"><input type="date" className="input" value={to} onChange={(e) => set({ ate: e.target.value || null, skip: null })} /></Field>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn sm" onClick={() => set({ tipo: null, prio: null, area: null, fatal: null, data: null, de: null, ate: null, ordem: null, dir: null, skip: null, ...(view !== "todas" && view !== "concluidas" ? { resp: null } : {}) })}>Limpar filtros</button>
            <span className="muted small">Os filtros ficam no endereço da página — dá para salvar o link nos favoritos.</span>
          </div>
        </div>
      )}
      {board.mode === "quadro" && board.meta && <>
        {view !== "concluidas" && <label className="row nowrap small" style={{ margin: "0 0 8px" }}><input type="checkbox" checked={search.get("concluidas") === "1"} onChange={(e) => set({ concluidas: e.target.checked ? "1" : null })} /> mostrar concluídas e canceladas (últimos 30 dias)</label>}
        <MenuBoard menu="atividades" meta={board.meta} filters={view === "concluidas"
          ? { view: "concluidas", q, responsibleUserId: responsible || undefined, doneDays: preset === "tudo" || preset === "outro" ? 0 : Number(preset) }
          : { view, q, responsibleUserId: responsible || undefined, showDone: search.get("concluidas") === "1" || undefined }} />
      </>}
      {board.mode === "lista" && <Card>
        <ErrorBox error={error} />
        <DataTable
          rows={data?.items}
          rowKey={(a) => a.id}
          onRowClick={(a) => navigate(`/atividades/${a.id}`)}
          rowClass={(a) => (a.fatalOverdue || a.overdue ? "row-bad" : a.state === "aguardando_dependencia" ? "row-waiting" : "")}
          empty={view === "minhas" ? "Nada pendente com você." : "Nenhuma atividade."}
          server={{ sort: { key: f.sort || null, dir: f.sort ? ((f.dir || (f.sort === "criacao" || f.sort === "numero" ? "desc" : "asc")) as "asc" | "desc") : null }, set: (x) => set({ ordem: x.key, dir: x.key ? x.dir : null, skip: null }) }}
          columns={[
            { key: "n", label: "#", width: 56, sortKey: "numero", render: (a) => a.number },
            { key: "t", label: "Atividade", sortKey: "titulo", render: (a) => <>{a.title}<div className="muted small">{typeOf(a.typeId)?.name ?? ""}</div></> },
            { key: "r", label: "Responsável", render: (a) => userName(a.responsibleUserId) },
            { key: "p", label: "Prioridade", width: 96, sortKey: "prioridade", render: (a) => <Badge tone={PRIORITY_TONE[a.priority] ?? "neutral"}>{a.priority}</Badge> },
            { key: "i", label: "Prazo interno", width: 110, sortKey: "interno", render: (a) => <span className={a.overdue ? "text-bad" : ""}>{fmtDate(a.internalDueAt)}</span> },
            { key: "f", label: "Prazo fatal", width: 100, sortKey: "fatal", render: (a) => <FatalDate own={a.fatalDueAt} related={a.relatedFatal} overdue={a.fatalOverdue} open={!["concluida", "cancelada"].includes(a.state)} /> },
            ...(view === "concluidas" ? [{ key: "c", label: "Encerramento", width: 220, sortKey: "encerramento", render: (a: ActivityRow) => <CompletedBy a={a} /> }] : [{ key: "s", label: "Situação", width: 140, sortKey: "situacao", render: (a: ActivityRow) => stateBadge(a.state, a.firstStartedAt) }]),
          ]}
        />
        {data && <Pager total={data.total} take={data.take} skip={data.skip} onChange={(s) => set({ skip: String(s) })} />}
      </Card>}
      {creating && <NewActivityModal onClose={() => { setCreating(false); set({ nova: null }); }} />}
    </div>
  );
}

// --------------------------------------------------------------- checklist

function ChecklistCard({ a, canEdit, onSaved }: { a: ActivityRow; canEdit: boolean; onSaved: () => void }): JSX.Element | null {
  const { userName } = useLookups();
  const [items, setItems] = useState(a.checklist ?? []);
  const [item, setItem] = useState("");
  useEffect(() => { setItems(a.checklist ?? []); }, [a.checklist]);
  const save = async (next: typeof items) => {
    setItems(next);
    try { await api.put(`/atividades/${a.id}/checklist`, { items: next.map(({ t, d }) => ({ t, d })) }); onSaved(); }
    catch (e) { setItems(a.checklist ?? []); toast.error((e as Error).message); }
  };
  if (!items.length && !canEdit) return null;
  const done = items.filter((x) => x.d).length;
  return (
    <Card title={<>Checklist {items.length > 0 && <span className="muted small">{done}/{items.length}</span>}</>}>
      <div className="stack" style={{ gap: 4 }}>
        {items.map((c, i) => (
          <label key={i} className="row nowrap">
            <input type="checkbox" checked={c.d} disabled={!canEdit} onChange={(e) => void save(items.map((x, j) => (j === i ? { ...x, d: e.target.checked } : x)))} />
            <span className="grow" style={{ textDecoration: c.d ? "line-through" : undefined }}>{c.t}</span>
            {c.d && c.by && <span className="muted small">{userName(c.by).split(" ")[0]}{c.at ? ` · ${fmtDateTime(c.at)}` : ""}</span>}
            {canEdit && <button type="button" className="link-btn small" onClick={() => void save(items.filter((_, j) => j !== i))}>tirar</button>}
          </label>
        ))}
        {canEdit && <input className="input" placeholder="+ item (Enter)" value={item} onChange={(e) => setItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && item.trim()) { void save([...items, { t: item.trim(), d: false }]); setItem(""); } }} />}
        {canEdit && <ChecklistPicker scope="atividade" compact ctx={{ activityId: a.id, typeId: a.typeId }} existing={items.map((x) => x.t)} onPick={(add) => add.length && void save([...items, ...add.map((t) => ({ t, d: false }))])} />}
      </div>
    </Card>
  );
}

// --------------------------------------------------------------- criação

export function NewActivityModal({ onClose, defaults, parent, onCreated }: {
  onClose: () => void; defaults?: { ctx?: EntityRef; typeKey?: string; title?: string; description?: string; fatalDueAt?: string; internalDueAt?: string | null }; parent?: { id: string; title: string };
  /** Criada a partir de outro lugar (post-it, chat): avisa e não navega. */
  onCreated?: (id: string) => void;
}): JSX.Element {
  const { catalog, me } = useLookups();
  const [ctx, setCtx] = useState<EntityRef | null>(defaults?.ctx ?? null);
  const defType = defaults?.typeKey ? catalog?.activityTypes.find((t) => t.key === defaults.typeKey)?.id ?? null : null;
  const [f, setF] = useState({
    title: (defaults?.title ?? "").slice(0, 255), description: defaults?.description ?? "", typeId: defType as string | null, responsibleUserId: (me?.id ?? null) as string | null,
    reviewerUserId: null as string | null, internalDueAt: defaults?.internalDueAt ?? "", plannedAt: "", fatalDueAt: defaults?.fatalDueAt ?? "", priority: "media",
  });
  const [calc, setCalc] = useState(false);
  // Calculadora (botão "Calcular" ou o botão flutuante): preenche as datas desta atividade.
  const fRef = useRef(f); fRef.current = f;
  const fillDates = (r: CalcApply) => setF({ ...fRef.current, fatalDueAt: r.fatalDueAt, ...(r.internalDueAt ? { internalDueAt: r.internalDueAt } : {}), description: fRef.current.description || r.note });
  const [filler] = useState(() => ({ fill: (r: CalcApply) => fillRef.current(r), processId: null as string | null }));
  const fillRef = useRef(fillDates); fillRef.current = fillDates;
  filler.processId = ctx?.kind === "processo" ? ctx.id : null;
  useActivityDateFill(filler);
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const F = useFields("atividade");
  // Checklist: o padrão do tipo + itens acrescentados na hora.
  const typeDef = catalog?.activityTypes.find((t) => t.id === f.typeId);
  const [checklist, setChecklist] = useState<string[] | null>(null);
  const items = checklist ?? typeDef?.defaultChecklist ?? [];
  const [newItem, setNewItem] = useState("");
  const missing = F.missing({ typeId: f.typeId, responsibleUserId: f.responsibleUserId, reviewerUserId: f.reviewerUserId, priority: f.priority, internalDueAt: f.internalDueAt, plannedAt: f.plannedAt, fatalDueAt: f.fatalDueAt, description: f.description });
  const draft = useDraft(`atividade:nova${parent ? `:${parent.id}` : defaults?.ctx ? `:${defaults.ctx.id}` : ""}`, { f, ctx }, (v) => { setF(v.f); setCtx(v.ctx); });
  const create = useAct(() => api.post<{ id: string }>("/atividades", {
    title: f.title, description: f.description || null, typeId: f.typeId, parentId: parent?.id ?? null,
    personId: !parent && ctx?.kind === "pessoa" ? ctx.id : null,
    caseId: !parent && ctx?.kind === "caso" ? ctx.id : null,
    processId: !parent && ctx?.kind === "processo" ? ctx.id : null,
    responsibleUserId: f.responsibleUserId, reviewerUserId: f.reviewerUserId,
    internalDueAt: f.internalDueAt || null, plannedAt: f.plannedAt || null, fatalDueAt: f.fatalDueAt || null,
    priority: f.priority,
    ...(checklist !== null || items.length ? { checklist: items } : {}),
  }), [["atividades"], ["painel"]], (r) => { draft.clear(); onClose(); if (onCreated) onCreated(r.id); else if (!parent) navigate(`/atividades/${r.id}`); });

  return (
    <Modal title={parent ? `Subatividade de: ${parent.title}` : "Nova atividade"} onClose={onClose} wide footer={
      <>
        <button className="btn" onClick={onClose}>Cancelar</button>
        {missing.length > 0 && <span className="muted small" style={{ marginRight: "auto" }}>Falta: {missing.join(", ")}</span>}
        <button className="btn primary" disabled={create.isPending || f.title.trim().length < 2 || (!parent && !ctx) || missing.length > 0} onClick={() => create.mutate()}>Criar</button>
      </>
    }>
      {draft.banner}
      <FormGrid cols={3}>
        {!parent && (
          <Field label="Vinculada a" required span={3} hint="Toda atividade pertence a uma pessoa, caso ou processo.">
            <EntityPicker kinds={["processo", "caso", "pessoa"]} value={ctx} onChange={setCtx} />
          </Field>
        )}
        <Field label="Título" required span={2}><input className="input" value={f.title} onChange={(e) => up({ title: e.target.value })} autoFocus /></Field>
        {F.show("typeId") && <Field label="Tipo" required={F.req("typeId")}><TypeSelect value={f.typeId} onChange={(v) => up({ typeId: v })} /></Field>}
        {F.show("responsibleUserId") && <Field label="Responsável" required={F.req("responsibleUserId")} hint="Vazio = vai para a fila"><UserSelect value={f.responsibleUserId} onChange={(v) => {
          // Estagiário: o advogado supervisor entra como revisor (dá para trocar).
          const sup = catalog?.users.find((u) => u.id === v)?.supervisorUserId ?? null;
          up({ responsibleUserId: v, ...(sup && !f.reviewerUserId ? { reviewerUserId: sup } : {}) });
        }} emptyLabel="Fila (sem responsável)" /></Field>}
        {F.show("reviewerUserId") && <Field label="Revisor" required={F.req("reviewerUserId")}><UserSelect value={f.reviewerUserId} onChange={(v) => up({ reviewerUserId: v })} emptyLabel="Sem revisão" /></Field>}
        {F.show("priority") && <Field label="Prioridade" required={F.req("priority")}>
          <select className="input" value={f.priority} onChange={(e) => up({ priority: e.target.value })}>
            <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
          </select>
        </Field>}
        {f.responsibleUserId && <div style={{ gridColumn: "1 / -1" }}><LoadStrip userId={f.responsibleUserId} selected={f.internalDueAt || f.plannedAt} onPick={(day) => up(f.internalDueAt ? { plannedAt: day } : { internalDueAt: day })} /></div>}
        {F.show("internalDueAt") && <Field label="Prazo interno" required={!!f.responsibleUserId || F.req("internalDueAt")}><input type="date" className="input" value={f.internalDueAt} onChange={(e) => up({ internalDueAt: e.target.value })} /></Field>}
        {F.show("plannedAt") && <Field label="Data planejada" required={F.req("plannedAt")} hint="Audiência, reunião…"><input type="date" className="input" value={f.plannedAt} onChange={(e) => up({ plannedAt: e.target.value })} /></Field>}
        {F.show("fatalDueAt") && <Field label="Prazo fatal" required={F.req("fatalDueAt")}>
          <div className="row nowrap"><input type="date" className="input" value={f.fatalDueAt} onChange={(e) => up({ fatalDueAt: e.target.value })} />
            <button type="button" className="btn small" title="Calculadora de prazos" onClick={() => setCalc(true)}>Calcular</button></div>
        </Field>}
        {calc && <DeadlineCalcModal onClose={() => setCalc(false)} processId={ctx?.kind === "processo" ? ctx.id : null}
          applyLabel="Preencher as datas da atividade" onApply={fillDates} />}
        {F.show("description") && <Field label="Descrição" required={F.req("description")} span={3}><textarea className="input" rows={3} value={f.description} onChange={(e) => up({ description: e.target.value })} /></Field>}
        <Field label={`Checklist${typeDef?.checklistRequired ? " (obrigatório para concluir)" : ""}`} span={3} hint={typeDef?.defaultChecklist?.length ? "Veio do tipo de atividade; dá para tirar ou acrescentar." : undefined}>
          <div className="stack" style={{ gap: 4 }}>
            <ChecklistPicker scope="atividade" existing={items}
              ctx={{ processId: parent ? null : ctx?.kind === "processo" ? ctx.id : null, caseId: parent ? null : ctx?.kind === "caso" ? ctx.id : null, activityId: parent?.id ?? null, typeId: f.typeId }}
              onPick={(add) => setChecklist([...items, ...add])} />
            {items.map((t, i) => <div key={i} className="row nowrap"><span>☐</span><span className="grow">{t}</span><button type="button" className="link-btn small" onClick={() => setChecklist(items.filter((_, j) => j !== i))}>tirar</button></div>)}
            <input className="input" placeholder="+ item do checklist (Enter)" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newItem.trim()) { e.preventDefault(); setChecklist([...items, newItem.trim()]); setNewItem(""); } }} />
          </div>
        </Field>
      </FormGrid>
      <div style={{ marginTop: 12 }}><ErrorBox error={create.error} /></div>
    </Modal>
  );
}

// --------------------------------------------------------------- detalhe

interface ActivityDetail extends ActivityRow {
  type: { id: string; key: string; name: string; requiresProcessReview: boolean; requiresEvidence: boolean } | null;
  process: { id: string; cnjNumber: string | null; title: string | null; officialLink: string | null; organName: string; phase: string; situation: string; instance: string | null; nature: string; followUp: string | null; nextFollowUpAt: string | null } | null;
  parent: { id: string; number: number; title: string; state: string } | null;
  children: ActivityRow[];
  dependsOn: Array<{ dependsOn: { id: string; number: number; title: string; state: string } }>;
  evidence: Record<string, unknown> | null;
  cancelReason: string | null;
}

export function ActivityDetailPage({ id }: { id: string }): JSX.Element {
  const { userName, can, me } = useLookups();
  const qc = useQueryClient();
  const { data: a, error } = useQuery({ queryKey: ["atividade", id], queryFn: () => api.get<ActivityDetail>(`/atividades/${id}`) });
  useTitle(a ? `#${a.number} ${a.title}` : "Atividade");
  type Dlg = null | "concluir" | "atribuir" | "fatal" | "devolver" | "cancelar" | "reabrir" | "editar" | "sub";
  // Vindo de um quadro (soltou em "Feito", "Cancelada"…): abre direto a tela certa.
  const [dlg, setDlg] = useState<Dlg>(() => {
    const a = new URLSearchParams(window.location.search).get("acao");
    return a === "concluir" || a === "cancelar" || a === "atribuir" ? a : null;
  });
  useEffect(() => {
    const ac = new URLSearchParams(window.location.search).get("acao");
    if (ac === "concluir" || ac === "cancelar" || ac === "atribuir") setDlg(ac);
  }, [id]);
  const back = new URLSearchParams(window.location.search).get("voltar");
  const closeDlg = () => { setDlg(null); if (back) navigate(back); };
  const inv = [["atividade", id], ["atividades"], ["painel"]];
  const act = (path: string) => (body: unknown) => api.post(`/atividades/${id}/${path}`, body);
  const startPause = withInvalidate(qc, inv, (p) => api.post(`/atividades/${id}/${p}`));

  if (error) return <ErrorBox error={error} />;
  if (!a) return <Loading />;
  const open = !["concluida", "cancelada"].includes(a.state);
  const ctxLink = a.processId ? `/processos/${a.processId}` : a.caseId ? `/casos/${a.caseId}` : a.personId ? `/pessoas/${a.personId}` : null;

  return (
    <div className="stack gap-16">
      <PageHeader
        title={<>#{a.number} {a.title}</>}
        subtitle={<>{a.type?.name ?? "Sem tipo"} · criada em {fmtDateTime(a.createdAt)} {ctxLink && <> · <Link to={ctxLink}>ver contexto</Link></>}</>}
        actions={
          <>
            {stateBadge(a.state, a.firstStartedAt)}
            {open && <button className="btn" onClick={() => setDlg("editar")}>Editar</button>}
            {open && can("atividade.atribuir") && <button className="btn" onClick={() => setDlg("atribuir")}>{a.responsibleUserId ? "Reatribuir" : "Atribuir"}</button>}
            {open && can("atividade.prazo_fatal.alterar") && <button className="btn" onClick={() => setDlg("fatal")}>Prazo fatal</button>}
            {a.state === "em_revisao" && a.reviewerUserId === me?.id && <button className="btn" onClick={() => setDlg("devolver")}>Devolver</button>}
            {a.state === "atribuida" && <button className="btn" onClick={() => void startPause("iniciar")}>Começar (fazendo)</button>}
            {a.state === "em_andamento" && <button className="btn" onClick={() => void startPause("pausar")}>Pausar</button>}
            {open && a.state !== "aguardando_dependencia" && <button className="btn primary" onClick={() => setDlg("concluir")}>{a.state === "em_revisao" ? "Aprovar e concluir" : "Concluir"}</button>}
            {open && <button className="btn danger" onClick={() => setDlg("cancelar")}>Cancelar</button>}
            {!open && <button className="btn" onClick={() => setDlg("reabrir")}>Reabrir</button>}
          </>
        }
      />
      <div className="grid side">
        <div className="stack gap-16">
          <FlowStepCard activityId={a.id} />
          <Card title="Detalhes">
            <dl className="kv">
              <dt>Responsável</dt><dd>{a.responsibleUserId ? userName(a.responsibleUserId) : <Badge tone="warn">na fila</Badge>}</dd>
              <dt>Revisor</dt><dd>{userName(a.reviewerUserId)}</dd>
              <dt>Prazo interno</dt><dd className={a.overdue || (open && isOverdue(a.internalDueAt)) ? "text-bad" : ""}>{fmtDate(a.internalDueAt)}</dd>
              <dt>Data planejada</dt><dd>{fmtDate(a.plannedAt)}</dd>
              <dt>Prazo fatal</dt><dd className={a.fatalDueAt ? "text-warn" : ""}>{a.fatalDueAt ? fmtDate(a.fatalDueAt) : a.relatedFatal
                ? <span className="text-warn">{fmtDate(a.relatedFatal.date)} <span className="muted small">— {relatedFatalTitle(a.relatedFatal).replace(/^Prazo fatal /, "")}{a.relatedFatal.activityId && <> (<Link to={`/atividades/${a.relatedFatal.activityId}`}>abrir</Link>)</>}</span></span>
                : "—"}</dd>
              <dt>Prioridade / risco</dt><dd>{a.priority} / {a.risk}</dd>
              {a.parent && (<><dt>Atividade principal</dt><dd><Link to={`/atividades/${a.parent.id}`}>#{a.parent.number} {a.parent.title}</Link></dd></>)}
              {a.dependsOn.length > 0 && (<><dt>Depende de</dt><dd>{a.dependsOn.map((d) => <div key={d.dependsOn.id}><Link to={`/atividades/${d.dependsOn.id}`}>#{d.dependsOn.number} {d.dependsOn.title}</Link> {stateBadge(d.dependsOn.state)}</div>)}</dd></>)}
              {a.completedAt && (<><dt>Concluída em</dt><dd>{fmtDateTime(a.completedAt)}</dd></>)}
              {a.completedBy && (<><dt>Concluída por</dt><dd>{userName(a.completedBy)}{a.completedBy !== a.responsibleUserId && <span className="tag tag-amber" style={{ marginLeft: 6 }}>não era o responsável ({userName(a.responsibleUserId)})</span>}</dd></>)}
              {a.cancelReason && (<><dt>Motivo do cancelamento</dt><dd>{a.cancelReason}</dd></>)}
              {a.pointsAwarded != null && (<><dt>Pontos</dt><dd><b>{Number(a.pointsAwarded).toLocaleString("pt-BR")}</b> para {userName(a.pointsUserId)}{a.pointsLate ? <span className="tag tag-amber" style={{ marginLeft: 6 }}>fora do prazo interno</span> : null}</dd></>)}
              {a.pointsAwarded == null && open && (a.type as { points?: number } | null)?.points ? (<><dt>Vale</dt><dd>{(a.type as { points?: number }).points} ponto(s) no prazo interno</dd></>) : null}
            </dl>
            {a.description && <><hr className="sep" /><div className="pre">{a.description}</div></>}
            {a.evidence && <><hr className="sep" /><div className="fld-label">Evidência</div><div className="json">{JSON.stringify(a.evidence, null, 2)}</div></>}
          </Card>
          <ChecklistCard a={a} canEdit={open} onSaved={() => void qc.invalidateQueries({ queryKey: ["atividade", id] })} />
          <Card title="Subatividades" actions={open && <button className="btn small" onClick={() => setDlg("sub")}>Adicionar</button>}>
            <DataTable rows={a.children} rowKey={(c) => c.id} onRowClick={(c) => navigate(`/atividades/${c.id}`)} empty="Nenhuma subatividade."
              columns={[
                { key: "n", label: "#", width: 56, render: (c) => c.number },
                { key: "t", label: "Título", render: (c) => c.title },
                { key: "r", label: "Responsável", render: (c) => userName(c.responsibleUserId) },
                { key: "f", label: "Prazo fatal", width: 100, render: (c) => <FatalDate own={c.fatalDueAt}
                  related={a.fatalDueAt ? { date: a.fatalDueAt, source: "principal", label: `#${a.number} ${a.title}`, activityId: a.id } : a.relatedFatal ?? null}
                  open={!["concluida", "cancelada"].includes(c.state)} /> },
                { key: "s", label: "Situação", render: (c) => stateBadge(c.state, c.firstStartedAt) },
              ]} />
          </Card>
          <ActivityMails activityId={a.id} />
          <DocumentsPanel ctx={{ activityId: a.id }} title="Anexos e evidências" />
        </div>
        <div className="stack gap-16">
          {a.process && (
            <Card title="Processo">
              <dl className="kv">
                <dt>Número</dt><dd><Link to={`/processos/${a.process.id}`}>{a.process.cnjNumber ?? a.process.title}</Link>{a.process.cnjNumber && <CopyButton text={a.process.cnjNumber} title="Copiar número do processo" />}</dd>
                <dt>Órgão</dt><dd>{a.process.organName}</dd>
                <dt>Natureza</dt><dd>{NATURE_LABEL[a.process.nature] ?? a.process.nature}</dd>
                <dt>Fase</dt><dd>{a.process.phase}</dd>
                <dt>Situação</dt><dd>{a.process.situation}</dd>
                {a.process.followUp && (<><dt>Acompanhamento</dt><dd>{a.process.followUp}</dd></>)}
              </dl>
              {a.process.officialLink && <a className="btn small" style={{ marginTop: 10 }} href={a.process.officialLink} target="_blank" rel="noreferrer">Abrir no tribunal</a>}
            </Card>
          )}
          {a.type?.requiresProcessReview && open && (
            <Card><div className="small">Ao concluir, será pedida a <strong>revisão da situação processual</strong> (fase, situação, acompanhamento).</div></Card>
          )}
        </div>
      </div>

      {dlg === "concluir" && <ConcludeDialog a={a} onClose={closeDlg} invalidate={inv} />}
      {dlg === "atribuir" && <AssignDialog a={a} onClose={closeDlg} invalidate={inv} />}
      {dlg === "editar" && <EditActivityDialog a={a} onClose={() => setDlg(null)} invalidate={inv} />}
      {dlg === "sub" && <NewActivityModal parent={{ id: a.id, title: a.title }} onClose={() => setDlg(null)} />}
      {dlg === "fatal" && (
        <FatalDialog a={a} onClose={() => setDlg(null)} submit={async (fatalDueAt, reason) => { await act("prazo-fatal")({ fatalDueAt, reason }); }} invalidate={inv} />
      )}
      {dlg === "devolver" && <ReasonDialog title="Devolver para ajustes" label="O que precisa ser ajustado" onClose={() => setDlg(null)} onSubmit={withInvalidate(qc, inv, (reason) => act("devolver")({ reason }))} />}
      {dlg === "cancelar" && <ReasonDialog title="Cancelar atividade" label="Motivo" onClose={closeDlg} onSubmit={withInvalidate(qc, inv, (reason) => act("cancelar")({ reason }))} confirm="Cancelar atividade"
        suggestions={["Cliente desistiu da ação", "Atividade duplicada", "Não é mais necessária", "Criada por engano"]}>
        <div className="muted small">A atividade fica encerrada como <b>cancelada (não feita)</b>: sai das pendências, aparece em Concluídas com essa marcação e não gera pontos. Dá para reabrir depois.</div>
      </ReasonDialog>}
      {dlg === "reabrir" && <ReasonDialog title="Reabrir atividade" onClose={() => setDlg(null)} onSubmit={withInvalidate(qc, inv, (reason) => act("reabrir")({ reason }))} />}
    </div>
  );
}

/** Envolve uma ação e invalida as consultas ao terminar (função comum, não hook). */
function withInvalidate(qc: QueryClient, keys: unknown[][], fn: (reason: string) => Promise<unknown>) {
  return async (reason: string) => {
    await fn(reason);
    await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
  };
}

function AssignDialog({ a, onClose, invalidate }: { a: ActivityDetail; onClose: () => void; invalidate: string[][] }): JSX.Element {
  const [user, setUser] = useState<string | null>(a.responsibleUserId);
  const [due, setDue] = useState(dateOnly(a.internalDueAt) || todayIso());
  const m = useAct(() => api.post(`/atividades/${a.id}/atribuir`, { responsibleUserId: user, internalDueAt: due }), invalidate, onClose);
  return (
    <Modal title="Atribuir" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Voltar</button><button className="btn primary" disabled={!user || !due || m.isPending} onClick={() => m.mutate()}>Atribuir</button></>}>
      <FormGrid>
        <Field label="Responsável" required><UserSelect value={user} onChange={setUser} allowEmpty={false} /></Field>
        <Field label="Prazo interno" required><input type="date" className="input" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
      </FormGrid>
      <div style={{ marginTop: 8 }}><LoadStrip userId={user} selected={due} onPick={setDue} /></div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function FatalDialog({ a, onClose, submit, invalidate }: { a: ActivityDetail; onClose: () => void; submit: (d: string | null, r: string) => Promise<void>; invalidate: string[][] }): JSX.Element {
  const [d, setD] = useState(dateOnly(a.fatalDueAt));
  const qc = useQueryClient();
  const run = withInvalidate(qc, invalidate, (reason) => submit(d || null, reason));
  return (
    <ReasonDialog title="Alterar prazo fatal" label="Justificativa (fica na auditoria)" min={5} onClose={onClose} onSubmit={run}>
      <Field label="Novo prazo fatal" hint="Deixe vazio para remover"><input type="date" className="input" value={d} onChange={(e) => setD(e.target.value)} /></Field>
      <div className="muted small">Atual: {fmtDate(a.fatalDueAt)}</div>
    </ReasonDialog>
  );
}

function EditActivityDialog({ a, onClose, invalidate }: { a: ActivityDetail; onClose: () => void; invalidate: string[][] }): JSX.Element {
  const [f, setF] = useState({
    title: a.title, description: a.description ?? "", reviewerUserId: a.reviewerUserId,
    internalDueAt: dateOnly(a.internalDueAt), plannedAt: dateOnly(a.plannedAt), priority: a.priority,
  });
  const m = useAct(() => api.patch(`/atividades/${a.id}`, {
    version: a.version, title: f.title, description: f.description || null, reviewerUserId: f.reviewerUserId,
    internalDueAt: f.internalDueAt || null, plannedAt: f.plannedAt || null, priority: f.priority,
  }), invalidate, onClose);
  return (
    <Modal title="Editar atividade" onClose={onClose} wide footer={<><button className="btn" onClick={onClose}>Voltar</button><button className="btn primary" disabled={m.isPending} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid cols={3}>
        <Field label="Título" span={3}><input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Revisor"><UserSelect value={f.reviewerUserId} onChange={(v) => setF({ ...f, reviewerUserId: v })} emptyLabel="Sem revisão" /></Field>
        <Field label="Prazo interno"><input type="date" className="input" value={f.internalDueAt} onChange={(e) => setF({ ...f, internalDueAt: e.target.value })} /></Field>
        <Field label="Data planejada"><input type="date" className="input" value={f.plannedAt} onChange={(e) => setF({ ...f, plannedAt: e.target.value })} /></Field>
        <Field label="Prioridade">
          <select className="input" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
          </select>
        </Field>
        <Field label="Descrição" span={3}><textarea className="input" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      </FormGrid>
      <div className="muted small pad">O prazo fatal só muda pelo botão “Prazo fatal”, com justificativa.</div>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function ConcludeDialog({ a, onClose, invalidate }: { a: ActivityDetail; onClose: () => void; invalidate: string[][] }): JSX.Element {
  // Protocolo de atividade sem processo (ex.: fluxo iniciado no caso): escolher/cadastrar o processo ou "ainda não há".
  const { catalog } = useLookups();
  const protoNoProc = !!a.type?.requiresProcessReview && !a.process && a.state !== "em_revisao";
  const [linkPid, setLinkPid] = useState<string>("");
  const [newProc, setNewProc] = useState(false);
  const procs = useQuery({
    queryKey: ["processos", "vincular", a.caseId, a.personId], enabled: protoNoProc && !!(a.caseId || a.personId),
    queryFn: () => api.get<{ items: Array<{ id: string; cnjNumber: string | null; internalIdentifier: string | null; title: string | null; organName: string }> }>(`/processos${qs({ caseId: a.caseId ?? undefined, personId: a.caseId ? undefined : a.personId ?? undefined, take: 50 })}`),
  });
  const linked = useQuery({
    queryKey: ["processo", linkPid], enabled: protoNoProc && !!linkPid && linkPid !== "__none",
    queryFn: () => api.get<NonNullable<ActivityDetail["process"]>>(`/processos/${linkPid}`),
  });
  const needsReview = !!a.type?.requiresProcessReview && a.state !== "em_revisao" && (!!a.process || (!!linkPid && linkPid !== "__none"));
  const needsEvidence = (!!a.type?.requiresEvidence || protoNoProc) && a.state !== "em_revisao";
  const p = a.process ?? linked.data ?? null;
  const [note, setNote] = useState("");
  const [protocol, setProtocol] = useState("");
  const flow = useFlowStep(a.id);
  const [outcome, setOutcome] = useState("");
  const [flowComment, setFlowComment] = useState("");
  const showOutcome = !!flow.data && flow.data.stepStatus === "ativo" && flow.data.outcomes.length > 0;
  const [rv, setRv] = useState({
    phase: p?.phase ?? "", situation: p?.situation ?? "", instance: p?.instance ?? "", organName: p?.organName ?? "",
    followUp: p?.followUp ?? "", nextFollowUpAt: dateOnly(p?.nextFollowUpAt), notes: "",
  });
  useEffect(() => {
    const d = linked.data;
    if (d) setRv({ phase: d.phase ?? "", situation: d.situation ?? "", instance: d.instance ?? "", organName: d.organName ?? "", followUp: d.followUp ?? "", nextFollowUpAt: dateOnly(d.nextFollowUpAt), notes: "" });
  }, [linked.data]);
  const m = useAct(async () => {
    if (showOutcome) await api.post(`/bpm/atividades/${a.id}/resultado`, { outcome, comment: flowComment || null });
    return conclude();
  }, [...invalidate, ["bpm-passo", a.id], ["bpm-execucoes"]], async (r) => {
    onClose();
    // Subatividade: a principal não fecha sozinha — pergunta.
    const pr = (r as { parentReady?: { id: string; number: number; title: string } | null })?.parentReady;
    if (pr && await confirmar(`Todas as subatividades de #${pr.number} "${pr.title}" estão concluídas. Concluir também a atividade principal?`, { confirm: "Concluir a principal" })) navigate(`/atividades/${pr.id}?acao=concluir`);
  });
  const conclude = () => api.post(`/atividades/${a.id}/concluir`, {
    ...(protoNoProc ? (linkPid === "__none" ? { noProcess: true } : { linkProcessId: linkPid }) : {}),
    note: note || null,
    evidence: needsEvidence || protocol ? { protocolo: protocol || null, observacao: note || null } : null,
    review: needsReview ? {
      phase: rv.phase, situation: rv.situation, instance: rv.instance || null, organName: rv.organName,
      followUp: rv.followUp || null, nextFollowUpAt: rv.nextFollowUpAt || null, notes: rv.notes || null,
    } : undefined,
  });

  return (
    <Modal title={a.state === "em_revisao" ? "Aprovar e concluir" : "Concluir atividade"} onClose={onClose} wide footer={
      <><button className="btn" onClick={onClose}>Voltar</button>
        <button className="btn primary" disabled={m.isPending || (needsEvidence && !protocol.trim()) || (protoNoProc && !linkPid) || flowOutcomeBlocked(flow.data, outcome, flowComment)} onClick={() => m.mutate()}>
          {a.reviewerUserId && a.state !== "em_revisao" ? "Enviar para revisão" : "Concluir"}
        </button></>
    }>
      <div className="stack">
        {showOutcome && flow.data && <OutcomePicker info={flow.data} outcome={outcome} setOutcome={setOutcome} comment={flowComment} setComment={setFlowComment} />}
        {protoNoProc && (
          <div className="stack gap-8" style={{ background: "var(--amber-50)", padding: 12, borderRadius: 8 }}>
            <div className="fld-label">Processo deste protocolo</div>
            <div className="muted small">Esta atividade está ligada {a.caseId ? "ao caso" : "à pessoa"}, não a um processo. Escolha o processo (a atividade{flow.data ? " e as demais tarefas do fluxo" : ""} passam a ficar nele), cadastre-o agora ou indique que ainda não existe (petição inicial).</div>
            <div className="row gap-8 nowrap">
              <select className="input" value={linkPid} onChange={(e) => setLinkPid(e.target.value)}>
                <option value="">Selecione…</option>
                {(procs.data?.items ?? []).map((x) => <option key={x.id} value={x.id}>{x.cnjNumber ?? x.internalIdentifier ?? "sem número"}{x.title ? ` — ${x.title}` : ""} · {x.organName}</option>)}
                <option value="__none">Ainda não há processo (petição inicial) — concluir só com o nº do protocolo</option>
              </select>
              {(a.caseId || a.personId) && <button type="button" className="btn" onClick={() => setNewProc(true)}>Cadastrar processo</button>}
            </div>
            {linkPid === "__none" && <div className="muted small">Depois, ao cadastrar o processo, vincule-o ao caso: o protocolo fica registrado nesta atividade.</div>}
          </div>
        )}
        {newProc && <NewProcessModal onClose={() => setNewProc(false)}
          caseRef={a.caseId ? { id: a.caseId, title: "Caso desta atividade" } : undefined}
          clientRef={!a.caseId && a.personId ? { id: a.personId, name: "Cliente desta atividade" } : undefined}
          onCreated={(id) => {
            setNewProc(false); void procs.refetch(); setLinkPid(id);
            // Protocolo de um caso: é o ajuizamento — o trabalho em andamento do caso passa para o processo.
            if (a.caseId) void applyCaseToProcess(a.caseId, id, { activities: true, flows: true, contracts: true, opposing: true, caseStageId: defaultCaseStage(catalog?.stages) }).catch((e: Error) => toast.error(e.message));
          }} />}
        {needsEvidence && (
          <Field label="Nº do protocolo (ID do protocolo)" required hint="Texto livre: o número/ID que o sistema do tribunal deu ao protocolo (ex.: 12345678 ou PJe 2026.0001234).">
            <input className="input" value={protocol} placeholder="(ID do protocolo)" onChange={(e) => setProtocol(e.target.value)} autoFocus />
          </Field>
        )}
        {needsReview && (
          <>
            <div className="fld-label">Revisão da situação processual</div>
            <div className="muted small">Confirme ou atualize — fica registrado mesmo sem mudança.</div>
            <FormGrid cols={3}>
              <Field label="Fase"><input className="input" value={rv.phase} onChange={(e) => setRv({ ...rv, phase: e.target.value })} /></Field>
              <Field label="Situação"><input className="input" value={rv.situation} onChange={(e) => setRv({ ...rv, situation: e.target.value })} /></Field>
              <Field label="Instância"><input className="input" value={rv.instance} onChange={(e) => setRv({ ...rv, instance: e.target.value })} /></Field>
              <Field label="Órgão" span={2}><input className="input" value={rv.organName} onChange={(e) => setRv({ ...rv, organName: e.target.value })} /></Field>
              <Field label="Próximo acompanhamento"><input type="date" className="input" value={rv.nextFollowUpAt} onChange={(e) => setRv({ ...rv, nextFollowUpAt: e.target.value })} /></Field>
              <Field label="O que acompanhar" span={3}><input className="input" value={rv.followUp} onChange={(e) => setRv({ ...rv, followUp: e.target.value })} /></Field>
              <Field label="Observações da revisão" span={3}><textarea className="input" rows={2} value={rv.notes} onChange={(e) => setRv({ ...rv, notes: e.target.value })} /></Field>
            </FormGrid>
          </>
        )}
        <Field label="Observação"><textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <ErrorBox error={m.error} />
      </div>
    </Modal>
  );
}
